import type { Attachment } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { Folder } from '@seen/core/models/document-references/folder'
import {
  Button,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@seen/ui'
import { IconLink, IconUpload } from '@tabler/icons-react'
import { type JSX, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CustomAttachmentButton } from '#src/components/button/CustomAttachmentButton.tsx'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { documentUploadErrorMessage } from '#src/components/document-reference/documentUploadErrorMessage.ts'
import { DuplicateFilesMessage } from '#src/components/document-reference/DuplicateFilesMessage.tsx'
import { useFolders } from '#src/components/document-reference/useFolders.ts'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import type { DuplicateFile } from '#src/utils/findDuplicateFiles.ts'

const createGoogleDriveAttachment = (url: string): Attachment => ({
  url,
  title: 'Google Drive Document',
  contentType: 'text/html',
})

/*
 * Special sentinel value used in the folder combobox to represent "no folder"
 * (i.e. General / unfiled). Using a string sentinel avoids the need for a
 * nullable generic on ComboboxButton.
 */
const GENERAL_FOLDER_VALUE = '__general__'

interface AddDocumentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (documentReference: ExtendedDocumentReference) => void
  initialFolder?: Folder
}

export function AddDocumentModal({
  open,
  onOpenChange,
  onSuccess,
  initialFolder,
}: AddDocumentModalProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const duplicateBlockEnabled = useFeatureFlag(
    featureFlags.documentUploadDuplicateBlock
  )
  const [googleDriveUrl, setGoogleDriveUrl] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const participant = useCurrentParticipant()
  const practitioner = useCurrentPractitioner()
  const allFolders = useFolders()
  const createDocumentMutation = useCreateMutation<ExtendedDocumentReference>()

  /* Only show folders the practitioner is permitted to access. */
  const accessibleFolders = useMemo(
    () => allFolders.filter((folder) => practitioner.isRole(...folder.roles)),
    [allFolders, practitioner]
  )

  /* Fall back to General (undefined) if initialFolder isn't accessible. */
  const validatedInitialFolder = useMemo(
    () =>
      initialFolder
        ? accessibleFolders.find((f) => f.id === initialFolder.id)
        : undefined,
    [initialFolder, accessibleFolders]
  )

  const [selectedFolder, setSelectedFolder] = useState<Folder | undefined>(
    validatedInitialFolder
  )

  /* Sync selection when the modal opens or the active folder tab changes. */
  const validatedFolderId = validatedInitialFolder?.id ?? null
  useEffect(() => {
    if (open) {
      setSelectedFolder(
        validatedFolderId
          ? accessibleFolders.find((f) => f.id === validatedFolderId)
          : undefined
      )
    }
  }, [open, validatedFolderId, accessibleFolders])

  const folderOptions = [
    { value: GENERAL_FOLDER_VALUE, label: 'General' },
    ...accessibleFolders.map((folder) => ({
      value: folder.id,
      label: folder.name,
    })),
  ]

  if (isReadOnly) {
    return null
  }

  const resetForm = (): void => {
    setGoogleDriveUrl('')
    setUploadError(null)
    setSelectedFolder(validatedInitialFolder)
  }

  const handleClose = (): void => {
    resetForm()
    onOpenChange(false)
  }

  const handleFolderChange = (value: string): void => {
    if (value === GENERAL_FOLDER_VALUE) {
      setSelectedFolder(undefined)
    } else {
      setSelectedFolder(accessibleFolders.find((f) => f.id === value))
    }
  }

  const validateGoogleDriveUrl = (url: string): boolean => {
    const googleServicesPattern =
      /^https:\/\/(drive|docs|sheets|slides)\.google\.com\//
    return googleServicesPattern.test(url)
  }

  const handleDuplicates = (
    duplicates: DuplicateFile[],
    uploadingCount: number
  ): void => {
    // The refusal is this attempt's result, so an earlier attempt's failure
    // must not stay on screen beside it.
    setUploadError(null)
    if (uploadingCount === 0) {
      setIsUploading(false)
    }
    const message = (
      <DuplicateFilesMessage
        documentsUrl={`${participant.relativeUrl}/documents`}
        duplicates={duplicates.map(({ file, document }) => ({
          fileName: file.name,
          documentId: document.id,
        }))}
      />
    )
    if (uploadingCount === 0) {
      toast.error(message)
    } else {
      toast.warning(message)
    }
  }

  const handleFileUpload = async (
    attachments: Attachment[],
    meta?: { fingerprint: string }
  ): Promise<void> => {
    setIsUploading(true)
    setUploadError(null)

    try {
      const doc = new ExtendedDocumentReference({
        subject: participant.reference,
        author: practitioner.reference,
        attachments,
      })
      doc.folder = selectedFolder
      if (meta) {
        doc.fingerprint = meta.fingerprint
      }

      const documentReference = await createDocumentMutation.mutateAsync(doc)

      toast.success('Document uploaded successfully')
      onSuccess?.(documentReference)
      handleClose()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      setUploadError(errorMessage)
      toast.error('Failed to upload document. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleLinkSubmit = async (): Promise<void> => {
    if (!googleDriveUrl) {
      toast.error('Please enter a URL')
      return
    }

    if (!validateGoogleDriveUrl(googleDriveUrl)) {
      toast.error('Please enter a valid Google Drive URL')
      return
    }

    setIsUploading(true)
    setUploadError(null)

    try {
      const attachment = createGoogleDriveAttachment(googleDriveUrl)

      const doc = new ExtendedDocumentReference({
        subject: participant.reference,
        author: practitioner.reference,
        attachments: [attachment],
      })
      doc.folder = selectedFolder

      const documentReference = await createDocumentMutation.mutateAsync(doc)

      toast.success('Google Drive link added successfully')
      onSuccess?.(documentReference)
      handleClose()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      setUploadError(errorMessage)
      toast.error('Failed to add Google Drive link. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader className="py-4 text-lg">
          <DialogTitle>Add Document</DialogTitle>
          <DialogDescription>
            Upload files or link to Google Drive documents
          </DialogDescription>
        </DialogHeader>
        <DialogContent className="pt-0">
          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload">Upload Files</TabsTrigger>
              <TabsTrigger value="link">Link to Google Drive</TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-4">
              {folderOptions.length > 1 && (
                <div>
                  <Label>Folder</Label>
                  <div className="mt-1">
                    <ComboboxButton
                      options={folderOptions}
                      value={selectedFolder?.id ?? GENERAL_FOLDER_VALUE}
                      onChange={handleFolderChange}
                      loading={isUploading}
                    />
                  </div>
                </div>
              )}
              <div className="text-center">
                <CustomAttachmentButton
                  disabled={isUploading}
                  onUpload={handleFileUpload}
                  duplicateCheck={
                    duplicateBlockEnabled
                      ? {
                          participant: participant.reference,
                          onDuplicates: handleDuplicates,
                        }
                      : undefined
                  }
                  onUploadStart={() => setIsUploading(true)}
                  onUploadError={(error) => {
                    setUploadError(documentUploadErrorMessage(error))
                    setIsUploading(false)
                    toast.error('Failed to upload document. Please try again.')
                  }}
                >
                  {({ onClick, onDrop, onDragOver }) => (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full flex-col gap-4 rounded-lg border-2 border-dashed border-gray-300 bg-transparent p-8 shadow-none hover:bg-transparent"
                      onClick={onClick}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      disabled={isUploading}
                    >
                      <IconUpload className="size-8 text-gray-400" />
                      <span className="text-gray-600">
                        Drop files here or click to browse
                      </span>
                      <span className="inline-flex items-center rounded-md border bg-background px-4 py-2 text-sm font-medium text-foreground">
                        <IconUpload className="mr-2 size-4" />
                        {isUploading ? 'Uploading...' : 'Choose Files'}
                      </span>
                    </Button>
                  )}
                </CustomAttachmentButton>
                {uploadError && (
                  <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="link" className="space-y-4">
              <div className="space-y-4">
                {folderOptions.length > 1 && (
                  <div>
                    <Label>Folder</Label>
                    <div className="mt-1">
                      <ComboboxButton
                        options={folderOptions}
                        value={selectedFolder?.id ?? GENERAL_FOLDER_VALUE}
                        onChange={handleFolderChange}
                        loading={isUploading}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="google-drive-url">Google Drive URL</Label>
                  <Input
                    id="google-drive-url"
                    type="url"
                    value={googleDriveUrl}
                    onChange={(e) => setGoogleDriveUrl(e.target.value)}
                    placeholder="https://drive.google.com/..."
                    disabled={isUploading}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Supports Google Drive, Docs, Sheets, and Slides
                  </p>
                </div>

                {uploadError && (
                  <p className="text-sm text-red-600">{uploadError}</p>
                )}

                <div className="space-x-2 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleLinkSubmit}
                    disabled={!googleDriveUrl || isUploading}
                  >
                    <IconLink className="mr-2 size-4" />
                    {isUploading ? 'Adding...' : 'Add Link'}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
